// One site's SOC setup: Ops form, Missions per event type, AI agent, Guard tours (Phase 4), Call procedure editor,
// Readiness and go-live. Phase 5 (features.deterrence): the site's Talk-down messages and Notifications (recipients,
// templates).

import { useState } from 'react';
import {
  api,
  errorText,
  type AuthorizedActivity,
  type CallConditions,
  type Contact,
  type ScheduleOverride,
  type SiteOps,
  type SiteSetup,
} from '../../api.ts';
import { ContactOptions, isUsable } from '../../components/ContactOptions.tsx';
import { Icon } from '../../components/Icon.tsx';
import { ErrorBox, InlineError, Loading, Pill } from '../../components/ui.tsx';
import { fmtPhone, human } from '../../console/util.ts';
import { useLoad } from '../../hooks.ts';
import { href, navigate } from '../../router.ts';
import { fmtLocal } from '../../time.ts';
import { useSession } from '../../session.tsx';
import { useRefreshOn } from '../../stream.tsx';
import { SiteAiPanel } from './AiEditor.tsx';
import { AiRulesEditor } from './AiRulesEditor.tsx';
import { GuardToursTab } from './GuardToursEditor.tsx';
import { NotificationsTab } from './NotificationsTab.tsx';
import { TalkdownLibrary } from './TalkdownEditor.tsx';
import { DAYS, isoToLocalInput, localInputToIso, moveItem, SaveBar, SkillPicker, Toggle, UpDown } from './common.tsx';

type Tab = 'ops' | 'missions' | 'ai' | 'tours' | 'calls' | 'talkdown' | 'notify' | 'ready';
const TABS: { key: Tab; label: string; agent?: boolean; deter?: boolean }[] = [
  { key: 'ops', label: 'Operations' },
  { key: 'missions', label: 'Missions' },
  { key: 'ai', label: 'AI agent', agent: true },
  { key: 'tours', label: 'Guard tours' },
  { key: 'calls', label: 'Call procedure' },
  { key: 'talkdown', label: 'Talk-down', deter: true },
  { key: 'notify', label: 'Notifications', deter: true },
  { key: 'ready', label: 'Readiness' },
];

function useSaver(setData: (d: SiteSetup) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const save = async (fn: () => Promise<SiteSetup>, after?: (d: SiteSetup) => void): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const d = await fn();
      setData(d);
      after?.(d);
      setSaved(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, saved, save, setSaved, setError };
}

/* ---------- operations ---------- */

function OpsForm({ id, setup, canEdit, setData }: { id: string; setup: SiteSetup; canEdit: boolean; setData: (d: SiteSetup) => void }) {
  const [d, setD] = useState<SiteOps>(setup.ops);
  const [pain, setPain] = useState('');
  const s = useSaver(setData);
  const skills = useLoad((signal) => api.skills(signal), []);
  const dirty = JSON.stringify(d) !== JSON.stringify(setup.ops);
  const set = <K extends keyof SiteOps>(k: K, v: SiteOps[K]) => {
    s.setSaved(false);
    setD((o) => ({ ...o, [k]: v }));
  };
  const setAct = (i: number, patch: Partial<AuthorizedActivity>) => set('authorizedActivity', d.authorizedActivity.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const setOv = (i: number, patch: Partial<ScheduleOverride>) => set('scheduleOverrides', d.scheduleOverrides.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const riskBad = d.riskLevel !== null && (d.riskLevel < 0 || d.riskLevel > 100);
  const ovBad = d.scheduleOverrides.some((o) => !o.from || !o.to || o.from >= o.to);

  return (
    <fieldset disabled={!canEdit} className="tabbody">
      <label className="field">
        <span className="lbl">Special instructions · shown to operators in amber</span>
        <textarea rows={4} value={d.specialInstructions ?? ''} onChange={(e) => set('specialInstructions', e.target.value || null)} />
      </label>

      <div className="field">
        <span className="lbl">Pain points</span>
        <div className="chips">
          {d.painPoints.map((p) => (
            <span key={p} className="chip">
              {p}
              {canEdit && (
                <button type="button" className="chipx" onClick={() => set('painPoints', d.painPoints.filter((x) => x !== p))} aria-label={`Remove ${p}`}>
                  <Icon name="x" />
                </button>
              )}
            </span>
          ))}
          {d.painPoints.length === 0 && <span className="note">None</span>}
        </div>
        {canEdit && (
          <div className="row-wrap">
            <input
              className="compact"
              value={pain}
              onChange={(e) => setPain(e.target.value)}
              placeholder="e.g. Catalytic converter theft"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pain.trim()) {
                  e.preventDefault();
                  if (!d.painPoints.includes(pain.trim())) set('painPoints', [...d.painPoints, pain.trim()]);
                  setPain('');
                }
              }}
              aria-label="New pain point"
            />
            <button
              type="button"
              className="btn sm"
              disabled={!pain.trim()}
              onClick={() => {
                if (!d.painPoints.includes(pain.trim())) set('painPoints', [...d.painPoints, pain.trim()]);
                setPain('');
              }}
            >
              <Icon name="plus" />
              Add
            </button>
          </div>
        )}
      </div>

      <div className="fgrid">
        <label className="field">
          <span className="lbl">Risk level (0–100)</span>
          <input type="number" min={0} max={100} value={d.riskLevel ?? ''} onChange={(e) => set('riskLevel', e.target.value === '' ? null : Number(e.target.value))} aria-invalid={riskBad} />
          {riskBad && <span className="inline-err">Use 0 to 100.</span>}
        </label>
        <label className="field">
          <span className="lbl">Risk reason</span>
          <input value={d.riskReason ?? ''} onChange={(e) => set('riskReason', e.target.value || null)} />
        </label>
        <label className="field">
          <span className="lbl">Customer tier</span>
          <input value={d.customerTier ?? ''} onChange={(e) => set('customerTier', e.target.value || null)} list="tiers" />
          <datalist id="tiers">
            <option value="standard" />
            <option value="premium" />
            <option value="enterprise" />
          </datalist>
        </label>
        <label className="field">
          <span className="lbl">Maintenance until (your time)</span>
          <input type="datetime-local" value={isoToLocalInput(d.maintenanceUntil)} onChange={(e) => set('maintenanceUntil', localInputToIso(e.target.value))} />
          <span className="note">Alarms auto-close with audit until then. Empty = off.</span>
        </label>
      </div>
      <Toggle checked={d.humanReviewOnly} onChange={(v) => set('humanReviewOnly', v)} label="Human review only (no AI resolution at this site)" disabled={!canEdit} />
      <Toggle
        checked={!!d.vmsVerifiedAutoResolve}
        onChange={(v) => set('vmsVerifiedAutoResolve', v)}
        label="Let the AI resolve events the EyeQ AI VMS verified as authorized activity (never as clear)"
        disabled={!canEdit}
      />

      <div className="field">
        <span className="lbl">Required skills · only operators with all of these are offered this site's events</span>
        {skills.error && !skills.data ? (
          <InlineError error={skills.error} />
        ) : !skills.data ? (
          <span className="note">Loading skills…</span>
        ) : (
          <SkillPicker skills={skills.data} value={d.requiredSkills ?? []} onChange={(v) => set('requiredSkills', v)} disabled={!canEdit} label="Required skills" />
        )}
        {(d.requiredSkills ?? []).length === 0 && <span className="note">None: any operator can take this site.</span>}
      </div>

      <div className="lbl">Event correlation</div>
      <div className="fgrid">
        {(
          [
            ['window_s', 'Group alarms into one event within (s)'],
            ['reopen_s', 'Link to a closed event within (s)'],
            ['burst_s', 'Collapse bursts within (s)'],
            ['storm_rate', 'Storm: alarms per 10 min'],
          ] as const
        ).map(([k, l]) => (
          <label key={k} className="field">
            <span className="lbl">{l}</span>
            <input type="number" min={0} value={d.correlation[k]} onChange={(e) => set('correlation', { ...d.correlation, [k]: Math.max(0, Number(e.target.value) || 0) })} />
          </label>
        ))}
      </div>

      <div className="sec">
        <div className="lbl">
          <span>Authorized activity</span>
          {canEdit && (
            <button type="button" className="btn sm" onClick={() => set('authorizedActivity', [...d.authorizedActivity, { label: '', days: [], start: '', end: '' }])}>
              <Icon name="plus" />
              Add
            </button>
          )}
        </div>
        {d.authorizedActivity.length === 0 && <div className="note">None. Add porters, cleaners, or vendors who are expected on site.</div>}
        {d.authorizedActivity.map((a, i) => (
          <div key={i} className="editrow">
            <input value={a.label} onChange={(e) => setAct(i, { label: e.target.value })} placeholder="Who (e.g. Night porter, white Tahoe)" aria-label="Who" />
            <div className="days" role="group" aria-label="Days">
              {DAYS.map((day) => {
                const on = a.days?.includes(day) ?? false;
                return (
                  <button key={day} type="button" className={`daybtn${on ? ' on' : ''}`} aria-pressed={on} onClick={() => setAct(i, { days: on ? (a.days ?? []).filter((x) => x !== day) : [...(a.days ?? []), day] })}>
                    {day.slice(0, 2)}
                  </button>
                );
              })}
            </div>
            <input type="time" value={a.start ?? ''} onChange={(e) => setAct(i, { start: e.target.value })} aria-label="From" className="compact" />
            <input type="time" value={a.end ?? ''} onChange={(e) => setAct(i, { end: e.target.value })} aria-label="To" className="compact" />
            <button
              type="button"
              className={`daybtn${a.covers === 'anyone' ? ' on' : ''}`}
              aria-pressed={a.covers === 'anyone'}
              title="Anyone: people and vehicles in general are expected in this window (a public lot, general traffic), so the AI may close ordinary activity. Off: only the party named."
              onClick={() => setAct(i, { covers: a.covers === 'anyone' ? 'named' : 'anyone' })}
            >
              Anyone
            </button>
            <input value={a.note ?? ''} onChange={(e) => setAct(i, { note: e.target.value })} placeholder="Note" aria-label="Note" />
            {canEdit && (
              <button type="button" className="btn icon sm" onClick={() => set('authorizedActivity', d.authorizedActivity.filter((_, j) => j !== i))} aria-label="Remove">
                <Icon name="trash" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="sec">
        <div className="lbl">
          <span>Schedule overrides</span>
          {canEdit && (
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                const now = new Date();
                const from = new Date(now.getTime() + 3600_000);
                const to = new Date(now.getTime() + 25 * 3600_000);
                set('scheduleOverrides', [...d.scheduleOverrides, { id: `SO-${crypto.randomUUID().slice(0, 8)}`, from: from.toISOString(), to: to.toISOString(), mode: 'monitor', reason: '' }]);
              }}
            >
              <Icon name="plus" />
              Add override
            </button>
          )}
        </div>
        {d.scheduleOverrides.length === 0 && <div className="note">No overrides. Contracted hours from the ERP apply.</div>}
        {d.scheduleOverrides.map((o, i) => (
          <div key={o.id} className="editrow">
            <input type="datetime-local" value={isoToLocalInput(o.from)} onChange={(e) => setOv(i, { from: localInputToIso(e.target.value) ?? '' })} aria-label="From" />
            <input type="datetime-local" value={isoToLocalInput(o.to)} onChange={(e) => setOv(i, { to: localInputToIso(e.target.value) ?? '' })} aria-label="To" />
            <select value={o.mode} onChange={(e) => setOv(i, { mode: e.target.value === 'suppress' ? 'suppress' : 'monitor' })} aria-label="Mode" className="compact">
              <option value="monitor">Monitor</option>
              <option value="suppress">Suppress</option>
            </select>
            <input value={o.reason} onChange={(e) => setOv(i, { reason: e.target.value })} placeholder="Reason" aria-label="Reason" />
            {canEdit && (
              <button type="button" className="btn icon sm" onClick={() => set('scheduleOverrides', d.scheduleOverrides.filter((_, j) => j !== i))} aria-label="Remove override">
                <Icon name="trash" />
              </button>
            )}
          </div>
        ))}
        {ovBad && <span className="inline-err">Each override needs a start before its end.</span>}
        <div className="note">Times are your local time. Overrides win over contracted hours.</div>
      </div>

      <SaveBar
        canEdit={canEdit}
        dirty={dirty}
        busy={s.busy}
        error={s.error}
        saved={s.saved}
        onReset={() => setD(setup.ops)}
        onSave={() =>
          !riskBad && !ovBad
            ? void s.save(
                () => api.patchSiteOps(id, { ...d, authorizedActivity: d.authorizedActivity.filter((a) => a.label.trim()) }),
                (n) => setD(n.ops),
              )
            : undefined
        }
      />
    </fieldset>
  );
}

/* ---------- missions ---------- */

function MissionsTab({ id, setup, canEdit, setData }: { id: string; setup: SiteSetup; canEdit: boolean; setData: (d: SiteSetup) => void }) {
  const templates = useLoad((signal) => api.missionTemplates(signal), []);
  const initial = setup.missions.map((m) => ({ eventType: m.eventType, templateId: m.templateId }));
  const [rows, setRows] = useState(initial);
  const s = useSaver(setData);
  const dirty = JSON.stringify(rows) !== JSON.stringify(initial);
  const types = [...new Set(['default', ...(templates.data ?? []).flatMap((t) => t.eventTypes), 'person', 'vehicle', 'line_crossing', 'loitering', 'intrusion', 'motion'])];
  const dup = rows.map((r) => r.eventType.trim()).filter((t, i, a) => t && a.indexOf(t) !== i);
  const empty = rows.some((r) => !r.eventType.trim() || !r.templateId);
  const setRow = (i: number, p: Partial<(typeof rows)[number]>) => {
    s.setSaved(false);
    setRows((l) => l.map((r, j) => (j === i ? { ...r, ...p } : r)));
  };
  return (
    <fieldset disabled={!canEdit} className="tabbody">
      <div className="banner">
        <Icon name="list" />
        <span>
          Each event type (an analytic, or <span className="mono">default</span>) gets a mission template. Events keep the template version they started with.
        </span>
        <a className="btn sm" href={href.setup('missions')}>
          Edit templates
        </a>
      </div>
      {templates.error ? <ErrorBox error={templates.error} onRetry={templates.reload} title="Could not load templates" /> : null}
      <datalist id="event-types">
        {types.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      {rows.length === 0 && <div className="note warn-t">No mission assigned. The site cannot go live without one.</div>}
      {rows.map((r, i) => {
        const cur = setup.missions.find((m) => m.eventType === r.eventType && m.templateId === r.templateId);
        return (
          <div key={i} className="editrow">
            <input value={r.eventType} onChange={(e) => setRow(i, { eventType: e.target.value })} list="event-types" placeholder="Event type" aria-label="Event type" className="mono" />
            <select value={r.templateId} onChange={(e) => setRow(i, { templateId: e.target.value })} aria-label="Mission template">
              <option value="">Choose a template…</option>
              {(templates.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · v{t.version}
                </option>
              ))}
              {!templates.data && r.templateId && <option value={r.templateId}>{cur?.templateName ?? r.templateId}</option>}
            </select>
            <span className="note mono">{cur ? `in use: v${cur.version}` : 'new'}</span>
            {canEdit && (
              <button type="button" className="btn icon sm" onClick={() => setRows((l) => l.filter((_, j) => j !== i))} aria-label="Remove mapping">
                <Icon name="trash" />
              </button>
            )}
          </div>
        );
      })}
      {canEdit && (
        <button type="button" className="btn sm self-start" onClick={() => setRows((l) => [...l, { eventType: l.some((x) => x.eventType === 'default') ? '' : 'default', templateId: '' }])}>
          <Icon name="plus" />
          Add event-type mission
        </button>
      )}
      {dup.length > 0 && <span className="inline-err">Each event type once: {dup.join(', ')}</span>}
      <SaveBar
        canEdit={canEdit}
        dirty={dirty}
        busy={s.busy}
        error={s.error}
        saved={s.saved}
        onReset={() => setRows(initial)}
        onSave={() =>
          !dup.length && !empty
            ? void s.save(
                () => api.putSiteMissions(id, rows.map((r) => ({ eventType: r.eventType.trim(), templateId: r.templateId }))),
                (n) => setRows(n.missions.map((m) => ({ eventType: m.eventType, templateId: m.templateId }))),
              )
            : s.setError(new Error(empty ? 'Every row needs an event type and a template.' : 'Each event type can appear once.'))
        }
      />
    </fieldset>
  );
}

/* ---------- call procedure ---------- */

interface CallRow {
  contactId: string;
  phoneId: string;
  analytics: string;
  start: string;
  end: string;
  /** From the server: contact or phone missing/inactive. */
  broken: boolean;
  name: string;
}

function toRows(setup: SiteSetup): CallRow[] {
  return setup.callProcedure
    .slice()
    .sort((a, b) => a.seq - b.seq)
    .map((s) => ({
      contactId: s.contactId,
      phoneId: s.phoneId,
      analytics: s.conditions?.analytics?.join(', ') ?? '',
      start: s.conditions?.window?.start ?? '',
      end: s.conditions?.window?.end ?? '',
      broken: s.broken,
      name: s.contactName,
    }));
}

function conditionsOf(r: CallRow): CallConditions | null {
  const analytics = r.analytics
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  const c: CallConditions = {};
  if (analytics.length) c.analytics = analytics;
  if (r.start && r.end) c.window = { start: r.start, end: r.end };
  return Object.keys(c).length ? c : null;
}

function CallsTab({ id, setup, canEdit, setData }: { id: string; setup: SiteSetup; canEdit: boolean; setData: (d: SiteSetup) => void }) {
  const initial = toRows(setup);
  const [rows, setRows] = useState<CallRow[]>(initial);
  const s = useSaver(setData);
  const strip = (l: CallRow[]) => l.map((r) => [r.contactId, r.phoneId, r.analytics, r.start, r.end]);
  const dirty = JSON.stringify(strip(rows)) !== JSON.stringify(strip(initial));
  const contacts = setup.contacts.filter(isUsable);
  const byId = new Map<string, Contact>(setup.contacts.map((c) => [c.id, c]));
  const setRow = (i: number, p: Partial<CallRow>) => {
    s.setSaved(false);
    setRows((l) => l.map((r, j) => (j === i ? { ...r, ...p } : r)));
  };
  const halfWindow = rows.some((r) => !!r.start !== !!r.end);
  const incomplete = rows.some((r) => !r.contactId || !r.phoneId);

  return (
    <fieldset disabled={!canEdit} className="tabbody">
      <div className="banner">
        <Icon name="lock" />
        <span>People and phone numbers come from the ERP. Here you set the order and when each one is called.</span>
      </div>
      {rows.length === 0 && <div className="note warn-t">No call procedure. Add at least one working contact before going live.</div>}
      {rows.map((r, i) => {
        const c = byId.get(r.contactId);
        const phone = c?.phones.find((p) => p.phone_id === r.phoneId);
        const broken = r.broken || !c || !isUsable(c) || (r.phoneId !== '' && !phone);
        return (
          <div key={i} className={`sopstep callrow${broken ? ' broken' : ''}`}>
            <UpDown i={i} n={rows.length} what="contact" disabled={!canEdit} onMove={(dlt) => setRows((l) => moveItem(l, i, dlt))} />
            <span className="mono dim">{i + 1}</span>
            <div className="callgrid">
              <select value={r.contactId} onChange={(e) => setRow(i, { contactId: e.target.value, phoneId: byId.get(e.target.value)?.phones.find((p) => p.is_primary)?.phone_id ?? byId.get(e.target.value)?.phones[0]?.phone_id ?? '', broken: false })} aria-label="Contact">
                <option value="">Choose a contact…</option>
                {!c && r.contactId && <option value={r.contactId}>{r.name} (missing)</option>}
                {c && !isUsable(c) && <option value={c.id}>{c.displayName} (inactive in the ERP)</option>}
                <ContactOptions contacts={contacts} />
              </select>
              <select value={r.phoneId} onChange={(e) => setRow(i, { phoneId: e.target.value, broken: false })} aria-label="Phone">
                <option value="">Choose a phone…</option>
                {c && !phone && r.phoneId && <option value={r.phoneId}>{r.phoneId} (missing)</option>}
                {(c?.phones ?? []).map((p) => (
                  <option key={p.phone_id} value={p.phone_id}>
                    {p.type} · {fmtPhone(p.e164, p.extension)}
                    {p.is_primary ? ' · primary' : ''}
                  </option>
                ))}
              </select>
              <input value={r.analytics} onChange={(e) => setRow(i, { analytics: e.target.value })} placeholder="All analytics (or e.g. person, vehicle)" aria-label="Only for analytics" className="mono" />
              <span className="row-wrap nowrap">
                <input type="time" value={r.start} onChange={(e) => setRow(i, { start: e.target.value })} aria-label="Call window start (site time)" className="compact" />
                –
                <input type="time" value={r.end} onChange={(e) => setRow(i, { end: e.target.value })} aria-label="Call window end (site time)" className="compact" />
              </span>
            </div>
            <span className="row-wrap">
              {broken ? <Pill tone="bad">broken</Pill> : <span className="typ">ERP contact</span>}
              {canEdit && (
                <button type="button" className="btn icon sm" onClick={() => setRows((l) => l.filter((_, j) => j !== i))} aria-label="Remove from call procedure">
                  <Icon name="trash" />
                </button>
              )}
            </span>
          </div>
        );
      })}
      {canEdit && (
        <button type="button" className="btn sm self-start" onClick={() => setRows((l) => [...l, { contactId: '', phoneId: '', analytics: '', start: '', end: '', broken: false, name: '' }])} disabled={contacts.length === 0}>
          <Icon name="plus" />
          Add from ERP contacts
        </button>
      )}
      {contacts.length === 0 && <div className="note">No ERP contacts for this site or its company.</div>}
      <div className="note">Empty analytics = every event type. The time window is site time; leave both empty for any time. Windows can cross midnight (22:00–06:00).</div>
      {halfWindow && <span className="inline-err">Give a window both a start and an end, or neither.</span>}
      <SaveBar
        canEdit={canEdit}
        dirty={dirty}
        busy={s.busy}
        error={s.error}
        saved={s.saved}
        onReset={() => setRows(initial)}
        onSave={() =>
          !halfWindow && !incomplete
            ? void s.save(
                () => api.putCallProcedure(id, rows.map((r) => ({ contactId: r.contactId, phoneId: r.phoneId, conditions: conditionsOf(r) }))),
                (n) => setRows(toRows(n)),
              )
            : s.setError(new Error(incomplete ? 'Every row needs a contact and a phone.' : 'Fix the call windows first.'))
        }
      />
    </fieldset>
  );
}

/* ---------- readiness ---------- */

function ReadinessTab({ id, setup, canEdit, setData, reload }: { id: string; setup: SiteSetup; canEdit: boolean; setData: (d: SiteSetup) => void; reload: () => void }) {
  const [reason, setReason] = useState('');
  const [undoing, setUndoing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const r = setup.readiness;
  const ok = r.checks.filter((c) => c.ok).length;

  const goLive = async () => {
    setBusy(true);
    setErr(null);
    try {
      setData(await api.goLive(id));
    } catch (e) {
      setErr(e);
      reload();
    } finally {
      setBusy(false);
    }
  };
  const undo = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      setData(await api.undoGoLive(id, reason.trim()));
      setUndoing(false);
      setReason('');
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tabbody">
      <div className={`banner ${setup.live ? 'ok' : 'warnish'}`}>
        <Icon name={setup.live ? 'check' : 'alert'} />
        <span>
          {setup.live
            ? `Live since ${fmtLocal(setup.goLiveAt)}. Its events go to operators.`
            : 'Commissioning: events from this site are shown to supervisors and admins only.'}{' '}
          Readiness {ok}/{r.checks.length}.
        </span>
        {canEdit &&
          (setup.live ? (
            !undoing && (
              <button type="button" className="btn" onClick={() => setUndoing(true)}>
                Undo go-live
              </button>
            )
          ) : (
            <button type="button" className="btn pri" onClick={() => void goLive()} disabled={busy} title={r.ok ? 'All checks pass' : 'Some checks fail; the server will refuse'}>
              <Icon name="check" />
              Go live
            </button>
          ))}
      </div>
      {undoing && (
        <div className="confirmbar danger">
          <span>Undo go-live? Events from this site go back to supervisors only.</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" aria-label="Reason" autoFocus />
          <button type="button" className="btn danger" onClick={() => void undo()} disabled={!reason.trim() || busy}>
            Undo go-live
          </button>
          <button type="button" className="btn" onClick={() => setUndoing(false)}>
            Cancel
          </button>
        </div>
      )}
      {err ? (
        <div className="errbox" role="alert">
          <Icon name="alert" />
          <div>
            <b>{setup.live ? 'Could not undo go-live' : 'Go-live refused'}</b>
            <div className="mono">{errorText(err)}</div>
          </div>
          <span />
        </div>
      ) : null}
      {r.checks.map((c) => (
        <div key={c.key} className="chk">
          <span className={`ck ${c.ok ? 'ok' : 'no'}`}>{c.ok ? <Icon name="check" /> : '!'}</span>
          <span>
            {c.label}
            {c.detail && <div className="note">{c.detail}</div>}
          </span>
          <span className="sp" />
          {!c.ok && <Pill tone="warn">Needs action</Pill>}
        </div>
      ))}
      <div className="sec">
        <div className="lbl">Authority</div>
        {setup.authority ? (
          <div className="ro">
            <span className="v">
              <span className="vv">
                <b>{setup.authority.name}</b> · {setup.authority.kind} · non-emergency {setup.authority.nonEmergency ?? '—'}
                <div className="note">{setup.authority.siteIds.includes(id) ? 'Assigned to this site' : 'Matched by the site location'}</div>
              </span>
              <a className="btn sm" href={href.setup('authorities', setup.authority.id)}>
                Open
              </a>
            </span>
          </div>
        ) : (
          <div className="note warn-t">No authority matches this site. Assign one under Authorities.</div>
        )}
      </div>
    </div>
  );
}

/* ---------- view ---------- */

export function SiteSetupView({ id, tab, canEdit }: { id: string; tab: string | null; canEdit: boolean }) {
  const load = useLoad((signal) => api.siteSetup(id, signal), [id]);
  // ERP changes (a new contact, hours entered) show up without a reload, as on the site page. Unsaved edits are kept:
  // the editors hold their rows in their own state, so only the lists they pick from are refreshed.
  useRefreshOn(['replica'], load.reload, 1000);
  const sess = useSession();
  const tabs = TABS.filter((x) => (!x.agent || sess.config.features.agent) && (!x.deter || sess.config.features.deterrence));
  const t: Tab = tabs.some((x) => x.key === tab) ? (tab as Tab) : 'ops';
  const data = load.data;
  if (load.error && !data) return <ErrorBox error={load.error} onRetry={load.reload} title="Could not load site setup" />;
  if (!data) return <Loading label="Loading site setup…" />;
  const r = data.readiness;
  const ok = r.checks.filter((c) => c.ok).length;
  const setData = (d: SiteSetup) => load.setData(d);
  return (
    <>
      <div className="page-h">
        <div>
          <h2>{data.site.name}</h2>
          <p>
            {data.site.companyName} · <span className="mono">{data.site.siteCode ?? data.site.id}</span> · {human(data.site.vertical, 'no vertical')} ·{' '}
            <Pill tone={data.live ? 'ok' : 'warn'}>{data.live ? 'Live' : 'Commissioning'}</Pill>
            <Pill tone={r.ok ? 'ok' : 'warn'}>
              Ready {ok}/{r.checks.length}
            </Pill>
          </p>
        </div>
        <span className="sp" />
        <a className="btn" href={href.site(id)}>
          <Icon name="db" />
          Master data
        </a>
      </div>
      <div className="stabs" role="tablist" aria-label="Site setup">
        {tabs.map((x) => (
          <button key={x.key} type="button" role="tab" aria-selected={t === x.key} className={t === x.key ? 'on' : ''} onClick={() => navigate(href.setup('sites', id, x.key), { replace: true })}>
            {x.label}
            {x.key === 'ready' && !r.ok && <span className="n hot">{r.checks.length - ok}</span>}
          </button>
        ))}
      </div>
      <InlineError error={load.error} />
      {t === 'ops' && <OpsForm id={id} setup={data} canEdit={canEdit} setData={setData} />}
      {t === 'missions' && <MissionsTab id={id} setup={data} canEdit={canEdit} setData={setData} />}
      {t === 'ai' && (
        <>
          <SiteAiPanel siteId={id} siteName={data.site.name} canEdit={canEdit} canPause={sess.has('supervisor', 'admin')} />
          {/* The site's AI rules: the same editor as Setup → AI policies → this site. */}
          <AiRulesEditor siteId={id} />
        </>
      )}
      {t === 'tours' && <GuardToursTab siteId={id} canEdit={canEdit} />}
      {t === 'calls' && <CallsTab id={id} setup={data} canEdit={canEdit} setData={setData} />}
      {t === 'talkdown' && <TalkdownLibrary siteId={id} siteName={data.site.name} />}
      {t === 'notify' && <NotificationsTab siteId={id} contacts={data.contacts} canEdit={canEdit} />}
      {t === 'ready' && <ReadinessTab id={id} setup={data} canEdit={canEdit} setData={setData} reload={load.reload} />}
    </>
  );
}
